import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "flex min-w-0 flex-col" }
const _hoisted_2 = ["title"]
const _hoisted_3 = ["aria-expanded"]
const _hoisted_4 = {
  key: 0,
  class: "ml-0.5 font-semibold text-gray-500"
}
const _hoisted_5 = ["aria-pressed", "title", "onClick"]
const _hoisted_6 = {
  key: 1,
  class: "ml-0.5 font-semibold text-gray-500"
}
const _hoisted_7 = {
  key: 1,
  class: "ml-auto self-center text-[11px] italic text-gray-400"
}

import { computed } from 'vue';
import { ChevronRight, Pin, X } from 'lucide-vue-next';
import { i18n } from '@/plugins/internationalization/i18n';
import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import colors from '@/plugins/tailwind';
import type { CameraReadoutEntry } from './cameraReadout';
import { isCameraMetric } from './cameraSeries';
import { productivityMetricGap, productivityMetricValue, PRODUCTIVITY_VALUE_PLACEHOLDER } from './productivityFormatting';
import { METRIC_KEYS, PRODUCTIVITY_METRICS, type MetricFlags, type MetricKey } from './productivityMetrics';
import { formatReadoutTimestamp, METRIC_FIGURE_FIELD, type ProductivityReadout, type ProductivityReferenceReadout } from './productivityReadout';

const READOUT_LABEL_CLASS = 'flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400';
// The position chip. `primary-300` is the palette's lightest shade - there is no softer brand tint to
// tone this down with - and it is a pale wash of the brand in all four solutions, so `primary-800`
// stays legible on it everywhere. Deliberately not the filled `primary-700` the metric toggles wear:
// this chip is not a control and must not invite the click that would prove it.
const PERIOD_CHIP_CLASS = 'w-fit rounded-full bg-primary-300 px-2 py-0.5 text-[10px] font-bold tracking-[0.3px] text-primary-800';
const READOUT_VALUE_CLASS = 'text-sm font-semibold tabular-nums text-gray-800';
// The date sits where every other cell carries a measurement, but it is not one - it names the point
// the measurements were taken at, which the chip above it has already named in the axis's own terms.
// So it is drawn back: smaller, grey, and at the reference figures' weight rather than the actuals'.
// The line height is held at the value row's own, or this cell would be shorter than the six beside
// it and, being centred, would lift its reference line off the level the other cells share.
const READOUT_DATE_CLASS = 'text-[11px] font-medium leading-[19px] tabular-nums text-gray-500';
const UNIT_CLASS = 'text-xs font-medium text-gray-400';
// The chart carries no legend, so this row is it. A swatch therefore has to say which *mark* on the
// chart a figure came from, not just which colour: a short rule for the metrics drawn as lines, a
// filled block for those drawn as columns. Colour alone is not enough: weight and Daily Gain are two
// shades of one green, and shape survives a reader who cannot separate the two reds or the two greens
// at all.
const LINE_SWATCH_CLASS = 'inline-block h-0.5 w-3.5 shrink-0 rounded-full';
const COLUMN_SWATCH_CLASS = 'inline-block h-2 w-2 shrink-0 rounded-[2px]';
// The reference line under every value, in the same muted weight the halo is drawn in, so the row
// reads as one bold row of actuals over one faint row of reference figures rather than as twelve
// figures of equal standing.
const REFERENCE_CLASS = 'text-[11px] font-medium tabular-nums text-gray-400';
// A camera's figure is subordinate to the Section figure it is read against, so it sits at the reference
// figures' weight - but not their colour, or at 11px the two stacks would read as one list of faint
// numbers with no telling where one ended.
const CAMERA_FIGURE_CLASS = '-mx-1 flex w-fit items-center gap-1.5 rounded px-1 text-[11px] font-medium tabular-nums text-gray-500 transition-all hover:bg-gray-200';
// The breakdown's own toggle, at the label row's size and weight because it captions what the cells
// opposite are showing rather than measuring anything.
const CAMERA_TOGGLE_CLASS = 'mt-0.5 inline-flex w-fit items-center gap-1 rounded text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400 transition-colors hover:text-gray-600';
// A camera whose curves are off the plot: a step back rather than a disappearance, since the entry is both
// the way back on and the explanation for why it was switched off. Hover takes it to full strength, for
// the reader asking what they are about to switch back on.
const CAMERA_FIGURE_HIDDEN_CLASS = 'opacity-50 hover:opacity-100';
// The swatch's colour once its curve is off the plot, at the weight the row's labels are drawn in. A token
// rather than a hex, so it moves with the palette the rest of the row is greyed against.
const CAMERA_NAME_CLASS = 'max-w-[9rem] truncate font-medium text-gray-400';
// Dashed, because the curve it stands for is dashed: a swatch says which *mark* on the plot a figure came
// from, and a camera is the third mark this chart draws. Wider and heavier than the metric swatches,
// because in the two cells that state no name it is the only thing identifying the camera and a finer dash
// spends most of its width on gaps.
const CAMERA_SWATCH_CLASS = 'inline-block w-4 shrink-0 border-t-[3px] border-dashed';
const PRODUCTIVITY_T = 'views.monitoring.activeGroups.modals.productivity';

export default /*@__PURE__*/_defineComponent({
  __name: 'ProductivityReadoutRow',
  props: {
    readout: {},
    granularity: {},
    periodPosition: {},
    pointing: { type: Boolean },
    visible: {},
    reference: {},
    pinned: { type: Boolean },
    cameras: {},
    camerasExpanded: { type: Boolean },
    hiddenSensorIds: {}
  },
  emits: ["release", "toggle-cameras", "toggle-camera"],
  setup(__props: any, { emit: __emit }) {

const HIDDEN_SWATCH_COLOR = colors.gray[400];
// The camera's name sits back from its figure: at equal weight the two words would outrank the number
// they belong to.
const PIN_HINT_KEY = `${PRODUCTIVITY_T}.readoutPinHint`;
const RESTING_KEY = `${PRODUCTIVITY_T}.readoutResting`;
const HIDE_TITLE_KEY = `${PRODUCTIVITY_T}.readoutCameraHide`;
const SHOW_TITLE_KEY = `${PRODUCTIVITY_T}.readoutCameraShow`;

const props = __props;

// `toggle-camera` names one camera and not the state it should end in, so a caller flipping the one it is
// told about cannot disagree with the row. By Sensor id - see resolveHiddenCameras.
const emit = __emit;

// Whether the metric cells are actually stating camera figures - one question, so no cell repeats both
// tests and builds a per-cell breakdown of nothing.
const camerasShown = computed(() => props.camerasExpanded && props.cameras.length > 0);

// The visible camera-reporting metric a reader reaches first, which is the cell that names the cameras.
// Derived from the cells on screen rather than fixed to weight, which is a toggle like any other.
const namingMetric = computed<MetricKey | null>(() => METRIC_KEYS.find((key) => props.visible[key] && isCameraMetric(key)) ?? null);

const dateLabel = computed(() => (props.readout ? formatReadoutTimestamp(props.readout.timestamp) : PRODUCTIVITY_VALUE_PLACEHOLDER));

const cells = computed(() =>
    METRIC_KEYS.filter((key) => props.visible[key]).map((key) => {
        const metric = PRODUCTIVITY_METRICS[key];
        // Null for a metric that is not a measurement at all - the Growth Index is a ratio against
        // a target, so no unit follows its figure.
        const unitKey = metric.unitKey(props.granularity);
        const actual = props.readout?.[METRIC_FIGURE_FIELD[key]] ?? null;
        const reference = props.reference?.[METRIC_FIGURE_FIELD[key]] ?? null;
        return {
            key,
            label: i18n.global.t(metric.labelKey) as string,
            color: metric.color,
            shape: metric.shape,
            unit: unitKey === null ? null : (i18n.global.t(unitKey) as string),
            value: productivityMetricValue(key, actual, props.granularity),
            referenceValue: productivityMetricValue(key, reference, props.granularity),
            referenceGap: productivityMetricGap(key, actual, reference, props.granularity),
            // This cell's figure broken down by camera, or nothing where the metric is not one a camera
            // reports. Formatted and gapped through the same functions the Section and reference figures
            // above go through, so the stack reads as one column of comparable numbers.
            //
            // The gap is against the SECTION figure, not the reference: a camera is part of this Section,
            // and what a reader wants is how far it sits from the whole. It is null where the Section
            // figure is withheld, and the camera figures beside that blank are the explanation for it.
            cameras:
                !camerasShown.value || !isCameraMetric(key)
                    ? []
                    : props.cameras.map((camera) => ({
                          ordinal: camera.ordinal,
                          sensorId: camera.sensorId,
                          color: camera.color,
                          hidden: props.hiddenSensorIds.includes(camera.sensorId),
                          label: key === namingMetric.value ? camera.label : null,
                          value: productivityMetricValue(key, camera[key], props.granularity),
                          gap: productivityMetricGap(key, camera[key], actual, props.granularity),
                      })),
        };
    })
);

// How far the measured figure sits from its norm. Null unless both are on screen and the norm is
// non-zero: a percentage of nothing is not a deviation, and quoting one against a hidden series
// would be reporting a comparison the reader cannot see either side of.
const waterDeviationPercent = computed(() => {
    if (!props.visible.water || !props.visible.waterNorm) return null;
    const actual = props.readout?.waterActual ?? null;
    const norm = props.readout?.waterNorm ?? null;
    if (actual === null || norm === null || norm === 0) return null;
    const percent = ((actual - norm) / norm) * 100;
    return Number.isFinite(percent) ? Math.round(percent) : null;
});

const formattedWaterDeviation = computed(() => (waterDeviationPercent.value === null ? PRODUCTIVITY_VALUE_PLACEHOLDER : (i18n.global.n(waterDeviationPercent.value, 'decimal') as string)));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["mb-3 flex flex-wrap gap-x-8 gap-y-2 rounded-lg border px-4 py-2.5 transition-colors", [_ctx.pinned ? 'border-primary-700 bg-gray-100' : 'border-gray-200 bg-gray-50', _ctx.camerasExpanded ? 'items-start' : 'items-center']])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (_ctx.periodPosition)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: _normalizeClass(PERIOD_CHIP_CLASS)
          }, _toDisplayString(_ctx.periodPosition), 1))
        : (_openBlock(), _createElementBlock("span", {
            key: 1,
            class: _normalizeClass(READOUT_LABEL_CLASS)
          }, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.readoutDate')), 1)),
      _createElementVNode("span", {
        class: _normalizeClass(READOUT_DATE_CLASS)
      }, _toDisplayString(dateLabel.value), 1),
      (_ctx.reference)
        ? (_openBlock(), _createElementBlock("span", {
            key: 2,
            class: _normalizeClass([REFERENCE_CLASS, "max-w-[11rem] truncate"]),
            title: _ctx.reference.name
          }, _toDisplayString(_ctx.reference.name), 9, _hoisted_2))
        : _createCommentVNode("", true),
      (_ctx.cameras.length > 0 && namingMetric.value !== null)
        ? (_openBlock(), _createElementBlock("button", {
            key: 3,
            type: "button",
            class: _normalizeClass(CAMERA_TOGGLE_CLASS),
            "aria-expanded": _ctx.camerasExpanded,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('toggle-cameras')))
          }, [
            _createVNode(_unref(ChevronRight), {
              class: _normalizeClass(["h-3 w-3 shrink-0 transition-transform", _ctx.camerasExpanded ? 'rotate-90' : '']),
              "aria-hidden": "true"
            }, null, 8, ["class"]),
            _createTextVNode(" " + _toDisplayString(_ctx.$tc('views.monitoring.activeGroups.modals.productivity.readoutCameras', _ctx.cameras.length, { count: _ctx.cameras.length })), 1)
          ], 8, _hoisted_3))
        : _createCommentVNode("", true)
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cells.value, (cell) => {
      return (_openBlock(), _createElementBlock("div", {
        key: cell.key,
        class: "flex flex-col"
      }, [
        _createElementVNode("span", {
          class: _normalizeClass(READOUT_LABEL_CLASS)
        }, [
          _createElementVNode("span", {
            class: _normalizeClass(cell.shape === 'line' ? LINE_SWATCH_CLASS : COLUMN_SWATCH_CLASS),
            style: _normalizeStyle({ backgroundColor: cell.color })
          }, null, 6),
          _createTextVNode(" " + _toDisplayString(cell.label), 1)
        ]),
        _createElementVNode("span", {
          class: _normalizeClass(READOUT_VALUE_CLASS)
        }, [
          _createTextVNode(_toDisplayString(cell.value) + " ", 1),
          (cell.unit)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: _normalizeClass(UNIT_CLASS)
              }, _toDisplayString(cell.unit), 1))
            : _createCommentVNode("", true),
          (cell.key === 'waterNorm' && waterDeviationPercent.value !== null)
            ? (_openBlock(), _createElementBlock("span", {
                key: 1,
                class: _normalizeClass(["ml-1.5 text-xs font-semibold", waterDeviationPercent.value > 0 ? 'text-sky-700' : 'text-gray-400'])
              }, _toDisplayString(waterDeviationPercent.value > 0 ? '+' : '') + _toDisplayString(formattedWaterDeviation.value) + " % ", 3))
            : _createCommentVNode("", true)
        ]),
        (_ctx.reference)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              class: _normalizeClass(REFERENCE_CLASS)
            }, [
              _createTextVNode(_toDisplayString(cell.referenceValue) + " ", 1),
              (cell.referenceGap !== null)
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(cell.referenceGap), 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cell.cameras, (camera) => {
          return (_openBlock(), _createElementBlock("button", {
            key: camera.ordinal,
            type: "button",
            class: _normalizeClass([CAMERA_FIGURE_CLASS, camera.hidden ? CAMERA_FIGURE_HIDDEN_CLASS : '']),
            "aria-pressed": !camera.hidden,
            title: _ctx.$t(camera.hidden ? SHOW_TITLE_KEY : HIDE_TITLE_KEY),
            onClick: ($event: any) => (emit('toggle-camera', camera.sensorId))
          }, [
            _createElementVNode("span", {
              class: _normalizeClass(CAMERA_SWATCH_CLASS),
              style: _normalizeStyle({ borderColor: camera.hidden ? _unref(HIDDEN_SWATCH_COLOR) : camera.color })
            }, null, 4),
            (camera.label !== null)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  class: _normalizeClass(CAMERA_NAME_CLASS)
                }, _toDisplayString(camera.label), 1))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(camera.value) + " ", 1),
            (camera.gap !== null)
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(camera.gap), 1))
              : _createCommentVNode("", true)
          ], 10, _hoisted_5))
        }), 128))
      ]))
    }), 128)),
    (_ctx.pinned)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "ml-auto self-center inline-flex items-center gap-1.5 rounded-full bg-primary-300 px-2.5 py-1 text-xs font-semibold text-primary-800 transition-colors hover:bg-primary-700 hover:text-white",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('release')))
        }, [
          _createVNode(_unref(Pin), {
            class: "h-3 w-3",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.readoutPinned')) + " ", 1),
          _createVNode(_unref(X), {
            class: "h-3 w-3",
            "aria-hidden": "true"
          })
        ]))
      : (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(_ctx.$t(_ctx.pointing ? PIN_HINT_KEY : RESTING_KEY)), 1))
  ], 2))
}
}

})